import { useEffect, useState } from 'react';
import { photoQueue } from '../platform/photo-queue.js';

/**
 * The queue-depth indicator — offline plan v1.1 §2.2 D, "never a lie".
 *
 * WHAT IT PROMISES. If this says nothing, nothing is waiting. If it says a number, that
 * many acts have been captured and not yet accepted by the server. It counts `queued`
 * and `sending` — work that is still going to happen — and reports failures and
 * expiries separately, because "2 waiting" and "2 stuck" are different sentences and
 * collapsing them is how a user learns to ignore the badge.
 *
 * WHY IT IS NOT A SPINNER. A spinner says "busy" and disappears; this has to be
 * legible while the phone is in a basement with no signal, which is the state it exists
 * for. It is deliberately quiet at zero — a permanent chrome element that usually says
 * "0" is one people stop reading.
 */
export function QueueBadge(): JSX.Element | null {
  const [, forceRender] = useState(0);

  useEffect(
    () =>
      photoQueue().subscribe(() => {
        forceRender((n) => n + 1);
      }),
    [],
  );

  const entries = photoQueue().entries();
  const pending = photoQueue().pendingCount();
  const stuck = entries.filter((e) => e.status === 'failed' || e.status === 'expired').length;

  if (pending === 0 && stuck === 0) return null;

  /**
   * ⚠️ THE QUEUE IS EVERY OFFLINE OP, NOT ONLY PHOTOS (0.1.507). `photoQueue()` kept its name
   * when it became the one op queue (flags, movements, intake ride it too), and this badge kept
   * calling every entry a photo — so a check-in captured in a basement read "1 photo waiting to
   * send", which is a lie about what is at stake (the product audit, 10 Sep 2026). The noun now
   * follows the contents: "photo(s)" only while every waiting op IS a photo upload, otherwise
   * "change(s)" — the one word that is true of all four kinds.
   */
  const allPhotos = (status: (s: string) => boolean): boolean =>
    entries.filter((e) => status(e.status)).every((e) => e.kind === 'photo-upload');
  const noun = (n: number, photos: boolean): string => (photos ? (n === 1 ? 'photo' : 'photos') : n === 1 ? 'change' : 'changes');
  const pendingNoun = noun(pending, allPhotos((s) => s === 'queued' || s === 'sending'));
  const stuckNoun = noun(stuck, allPhotos((s) => s === 'failed' || s === 'expired'));

  return (
    <div className="queue-badge" role="status" aria-live="polite">
      {pending > 0 && (
        <span className="queue-badge__pending">{`${String(pending)} ${pendingNoun} waiting to send`}</span>
      )}
      {stuck > 0 && (
        <span className="queue-badge__stuck">{`${String(stuck)} ${stuckNoun} need${stuck === 1 ? 's' : ''} attention`}</span>
      )}
    </div>
  );
}
